import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { authAPI } from '../services/api';
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';

const OnboardingModal = ({ onComplete }) => {
    const { user, updateUser } = useAuth();
    const { isDark } = useTheme();
    const navigate = useNavigate();
    const [finishing, setFinishing] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);

    const { width, height } = useWindowSize();

    const handleFinish = async (destination = '/channels') => {
        try {
            setFinishing(true);
            setShowConfetti(true);

            // Mark onboarding complete
            await authAPI.completeOnboarding();

            // Update local user context
            updateUser({ ...user, onboarded: true });

            setTimeout(() => {
                setShowConfetti(false);
                onComplete();
                navigate(destination);
            }, 1200);
        } catch (error) {
            console.error('Failed to complete onboarding:', error);
            setFinishing(false);
            setShowConfetti(false);
        }
    };

    const handleSkip = async () => {
        try {
            await authAPI.completeOnboarding();
            updateUser({ ...user, onboarded: true });
            onComplete();
        } catch (error) {
            console.error('Failed to skip onboarding:', error);
            onComplete();
        }
    };

    const firstName = user?.full_name?.split(' ')[0] || 'there';

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {showConfetti && <Confetti width={width} height={height} numberOfPieces={300} recycle={false} gravity={0.2} />}

            {/* Blurred Background */}
            <div className="absolute inset-0 bg-black/70 backdrop-blur-md animate-[fadeIn_0.3s_ease-out]"></div>

            {/* Modal Box */}
            <div className={`relative w-full max-w-md rounded-2xl border shadow-2xl p-8 overflow-hidden transform animate-[scaleIn_0.3s_ease-out] ${isDark ? 'bg-dark-800 border-white/10' : 'bg-white border-teal/20'}`}>

                {/* Skip Button */}
                {!finishing && (
                    <button onClick={handleSkip} className={`absolute top-4 right-4 text-xs font-bold uppercase tracking-widest transition-opacity ${isDark ? 'text-light/30 hover:text-light/70' : 'text-darkTeal/30 hover:text-darkTeal/70'}`}>
                        Skip
                    </button>
                )}

                <div className="flex flex-col items-center text-center animate-[fadeIn_0.3s_ease-out] min-h-[300px]">
                    {/* Icon */}
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-6 border-4 ${isDark ? 'bg-navy border-cyan/20' : 'bg-mint border-primary/20'}`}>
                        <span className={`material-symbols-outlined text-3xl ${isDark ? 'text-cyan' : 'text-primary'}`}>waving_hand</span>
                    </div>

                    <h2 className={`text-2xl font-black mb-3 ${isDark ? 'text-light' : 'text-darkTeal'}`}>
                        Welcome, {firstName}!
                    </h2>
                    <p className={`text-sm mb-6 max-w-sm leading-relaxed ${isDark ? 'text-light/60' : 'text-darkTeal/60'}`}>
                        Let's get your account set up. First, connect your email to start capturing leads automatically.
                    </p>

                    {/* Steps Preview */}
                    <div className={`w-full rounded-xl border p-4 mb-8 text-left space-y-3 ${isDark ? 'bg-navy border-white/5' : 'bg-mint border-teal/10'}`}>
                        {[
                            { icon: 'mail', label: 'Connect your email inbox', color: isDark ? 'text-cyan' : 'text-primary' },
                            { icon: 'tune', label: 'Enable your lead sources', color: 'text-emerald-500' },
                            { icon: 'bolt', label: 'Leads auto-captured & responded', color: 'text-amber-500' },
                        ].map((item, i) => (
                            <div key={i} className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-teal/20'}`}>
                                    <span className={`material-symbols-outlined text-lg ${item.color}`}>{item.icon}</span>
                                </div>
                                <span className={`text-sm font-medium ${isDark ? 'text-light/80' : 'text-darkTeal/80'}`}>{item.label}</span>
                            </div>
                        ))}
                    </div>

                    {/* Primary CTA */}
                    <button
                        onClick={() => handleFinish('/channels')}
                        disabled={finishing}
                        className={`w-full py-3.5 rounded-xl text-sm font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all ${finishing ? 'opacity-70 cursor-wait' : 'hover:scale-[1.02]'} ${isDark ? 'bg-cyan text-navy shadow-lg shadow-cyan/20' : 'bg-primary text-white shadow-lg shadow-primary/20'}`}
                    >
                        {finishing ? 'Setting up...' : 'Connect Your Email'}
                        {!finishing && <span className="material-symbols-outlined text-lg">arrow_forward</span>}
                    </button>

                    <button
                        onClick={() => handleFinish('/lead-sources')}
                        disabled={finishing}
                        className={`mt-3 text-xs font-bold uppercase tracking-widest transition-colors ${isDark ? 'text-light/30 hover:text-light/60' : 'text-darkTeal/30 hover:text-darkTeal/60'}`}
                    >
                        I'll connect later, show me lead sources →
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OnboardingModal;
